import { type ClientAccountsResp, type Platforms } from 'shared/api/accounts'
import {
  isSupportedFlowDatasourcePlatform,
  OAUTH_ADS_FLOW_DATASOURCES,
  type FlowDatasourceEntity,
  type FlowDatasourceEntityType,
  type OauthAdsAlviePlatform,
  type SupportedFlowDatasourcePlatform
} from 'shared/copilot/flowDataAgentConstants'

type IdBasedAdsAccountEntityType = Extract<
  FlowDatasourceEntityType,
  (typeof OAUTH_ADS_FLOW_DATASOURCES)[number]['entityType']
>

const buildIdBasedAdsAccountEntity = (
  account: ClientAccountsResp,
  type: IdBasedAdsAccountEntityType,
  credentialsPlatform: OauthAdsAlviePlatform,
  accountId: string = account.externalAccountId
): FlowDatasourceEntity => ({
  id: accountId,
  type,
  name: account.name,
  data: {
    id: accountId,
    name: account.name,
    credentials_id: account.credentialsId,
    platform: credentialsPlatform
  }
})

const toGa4PropertyResourceName = (externalAccountId: string): string => {
  if (externalAccountId.startsWith('properties/')) {
    return externalAccountId
  }
  return `properties/${externalAccountId}`
}

const toMetaAdsAccountId = (externalAccountId: string): string => {
  if (externalAccountId.startsWith('act_')) {
    return externalAccountId
  }
  return `act_${externalAccountId}`
}

const buildGoogleAdsEntity = (account: ClientAccountsResp): FlowDatasourceEntity => {
  const customerId = account.externalAccountId

  return {
    id: customerId,
    type: 'google_ads_account',
    name: account.name,
    data: {
      customer_id: customerId,
      account_name: account.name,
      credentials_id: account.credentialsId,
      platform: 'google',
      currency_code: account.currencyCode ?? '',
      // Not the real Google Ads status but our own view of the account: Google
      // refuses to describe an account that is not active, so the lookup that
      // fills the other fields can only ever report 'enabled'. Storing that
      // would be storing a constant. The agent can read the live status with
      // its account-info tool if it ever needs it.
      status: account.connected ? 'ENABLED' : 'DISABLED',
      is_manager: account.canManageClients,
      // Null until the account metadata sync reaches this account. Flow does
      // not resolve it — the agent refuses a query without it — so the data
      // source picker disables such an account and asks the user to reconnect
      // it before a chat can start.
      login_customer_id: account.loginCustomerId ?? null
    }
  }
}

const buildGa4Entity = (account: ClientAccountsResp): FlowDatasourceEntity => {
  const propertyResourceName = toGa4PropertyResourceName(account.externalAccountId)

  return {
    id: propertyResourceName,
    type: 'ga4_property',
    name: account.name,
    data: {
      id: propertyResourceName,
      name: account.name,
      credentials_id: account.credentialsId,
      platform: 'google'
    }
  }
}

const buildGscEntity = (account: ClientAccountsResp): FlowDatasourceEntity => {
  const siteUrl = account.externalAccountId

  return {
    id: siteUrl,
    type: 'gsc_property',
    name: account.name,
    data: {
      site_url: siteUrl,
      name: account.name,
      credentials_id: account.credentialsId,
      platform: 'google'
    }
  }
}

const buildOauthAdsEntity = (
  { alviePlatform, entityType }: (typeof OAUTH_ADS_FLOW_DATASOURCES)[number],
  account: ClientAccountsResp
): FlowDatasourceEntity => {
  const accountId =
    alviePlatform === 'facebook' ? toMetaAdsAccountId(account.externalAccountId) : account.externalAccountId
  return buildIdBasedAdsAccountEntity(account, entityType, alviePlatform, accountId)
}

const oauthAdsPlatformBuilders = Object.fromEntries(
  OAUTH_ADS_FLOW_DATASOURCES.map((entry) => [
    entry.alviePlatform,
    (account: ClientAccountsResp) => buildOauthAdsEntity(entry, account)
  ])
) as Record<OauthAdsAlviePlatform, (account: ClientAccountsResp) => FlowDatasourceEntity>

const PLATFORM_ENTITY_BUILDERS: Record<
  SupportedFlowDatasourcePlatform,
  (account: ClientAccountsResp) => FlowDatasourceEntity
> = {
  google: buildGoogleAdsEntity,
  ga4: buildGa4Entity,
  google_search_console: buildGscEntity,
  ...oauthAdsPlatformBuilders
}

/** Maps an Alvie client account to a Flow datasource entity. */
export const mapAlvieAccountToFlowEntity = (
  account: ClientAccountsResp,
  platform: SupportedFlowDatasourcePlatform
): FlowDatasourceEntity => {
  return PLATFORM_ENTITY_BUILDERS[platform](account)
}

export const getDatasourceOptionId = (account: ClientAccountsResp): string => `${account.platform}:${account.id}`

export const parseDatasourceOptionId = (
  optionId: string
): { platform: SupportedFlowDatasourcePlatform; accountId: number } | null => {
  const separatorIndex = optionId.indexOf(':')
  if (separatorIndex <= 0) {
    return null
  }
  const platform = optionId.slice(0, separatorIndex) as Platforms
  const accountId = Number(optionId.slice(separatorIndex + 1))
  if (!Number.isFinite(accountId)) {
    return null
  }
  if (!isSupportedFlowDatasourcePlatform(platform)) {
    return null
  }
  return { platform, accountId }
}
